import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getUserAttributes } from '@mb/lib';
import useEmblaCarousel from 'embla-carousel-react';
import Arrow, { ArrowDirection } from './Arrow';
import * as Styled from './ImageGallery.styles';
import { useTranslateCollections } from '../../../Banners/i18n';
import {
  AnalyticsEventAction,
  AnalyticsEventName,
  sendAnalyticsEvent,
} from '../../helpers/analitycs';
import { goToLogin, openSaveToBoardModal } from '../../helpers/saveToBoard';
import { useEmblaApi } from '../../hooks/useEmblaApi';
import { GalleryContext } from '../../hooks/useGalleryContext';
import { Image, Campaign } from '../../types';
import { FullscreenGallery } from '../FullscreenGallery/FullscreenGallery';

type ImageCarouselProps = {
  campaign: Campaign | null;
};

export const ImageCarousel: React.FC<ImageCarouselProps> = ({ campaign }) => {
  const t = useTranslateCollections();
  const images = campaign?.header_inspiration_images ?? [];
  const [emblaRef, emblaApi] = useEmblaCarousel();
  const [selectedSnap, setSelectedSnap] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const {
    galleryActive,
    setGalleryActive,
    fullScreenActive,
    setFullScreenActive,
  } = useContext(GalleryContext);
  const { snapCount } = useEmblaApi(
    emblaApi,
    setSelectedSnap,
    setGalleryActive,
  );
  const userData = getUserAttributes();
  const commonAnalyticsParams = {
    event: AnalyticsEventName.CAROUSEL,
    name: 'Inspiration image carousel',
    experiment_name: null,
    experiment_version: null,
    event_location: null,
    method: null,
    list_id: null,
    list_name: null,
    type: null,
  };

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
    sendAnalyticsEvent({
      ...commonAnalyticsParams,
      action: AnalyticsEventAction.BACK,
    });
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
    sendAnalyticsEvent({
      ...commonAnalyticsParams,
      action: AnalyticsEventAction.FORWARD,
    });
  }, [emblaApi]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (fullScreenActive) return;
      if (e.key === 'ArrowLeft') scrollPrev();
      if (e.key === 'ArrowRight') scrollNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [scrollPrev, scrollNext]);

  const handleImageClick = useCallback(
    (image: Image, index: number) => {
      setFullScreenActive(() => {
        setCurrentImageIndex(index);
        return true;
      });

      sendAnalyticsEvent({
        ...commonAnalyticsParams,
        event: AnalyticsEventName.SELECT_CONTENT,
        content_id: image.id,
        content_type: 'Curated collection inspiration image',
        name: image.name,
      });
    },
    [setSelectedSnap, setFullScreenActive],
  );

  const handleSaveToBoard = useCallback((e: React.MouseEvent, image: Image) => {
    e.stopPropagation();

    if (userData.signin_token == null) {
      goToLogin();
      return;
    }

    openSaveToBoardModal({
      imageUrl: image.url,
      fileName: image.name,
      onSuccessAnalyticsEvents: [
        { ecommerce: null },
        {
          ...commonAnalyticsParams,
          event: AnalyticsEventName.ADD_TO_WISHLIST,
          content_id: image.id,
          content_type: 'Curated collection inspiration image',
          name: image.name,
          method: 'Single',
        },
      ],
    });
  }, []);

  if (images.length === 0) return null;

  return (
    <Styled.Wrapper>
      <FullscreenGallery
        isActive={fullScreenActive}
        images={images}
        currentImageIndex={currentImageIndex}
      />
      <Arrow
        direction={ArrowDirection.LEFT}
        onClick={scrollPrev}
        show={galleryActive}
      />
      <Arrow
        direction={ArrowDirection.RIGHT}
        onClick={scrollNext}
        show={selectedSnap !== snapCount - 1}
      />
      <Styled.Viewport isActive={galleryActive}>
        <div ref={emblaRef}>
          <Styled.Content>
            {images.map(({ image }, index) => (
              // @ts-expect-error // TODO: fix types
              <Styled.Item
                key={image[0].id + index}
                onClick={() => handleImageClick(image[0], index)}
              >
                <Styled.SaveToBoardButton
                  size="medium"
                  variant="contained"
                  color="secondary"
                  onClick={(e) => handleSaveToBoard(e, image[0])}
                >
                  {t('Add to Board')}
                </Styled.SaveToBoardButton>
                <img src={image[0].url} alt="Inspiration image" />
                <Styled.FullScreenButton iconName="full-screen" />
              </Styled.Item>
            ))}
          </Styled.Content>
        </div>
      </Styled.Viewport>
    </Styled.Wrapper>
  );
};
